import { useState } from 'react';
import { Icon } from '@iconify/react';
import pinFill from '@iconify/icons-eva/pin-fill';
import shareFill from '@iconify/icons-eva/share-fill';
import { alpha } from '@material-ui/core/styles';
import { Box, CircularProgress, Tooltip } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { MIconButton } from '../../components/@material-extend';
import useAuth from 'src/hooks/useAuth';
import { REST_API } from 'src/constants/DefaultValues';

const buildMapsLink = (latitude, longitude) =>
  `https://maps.google.com/?q=${latitude},${longitude}`;

export default function LocationShareButton() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const shareLocation = async (latitude, longitude, locationName) => {
    const mapsUrl = buildMapsLink(latitude, longitude);
    const shareText = locationName
      ? `My current location: ${locationName}\n${mapsUrl}`
      : `My current location:\n${mapsUrl}`;

    // Customer app WebView: open native share sheet (WhatsApp, etc.)
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'SHARE_CONTENT',
          title: 'My Current Location',
          message: shareText,
          url: mapsUrl,
        })
      );
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Current Location',
          text: shareText,
          url: mapsUrl,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      enqueueSnackbar('Location link copied to clipboard', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(mapsUrl, { variant: 'info' });
    }
  };

  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      enqueueSnackbar('Location is not supported on this device', { variant: 'error' });
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        let locationName = null;

        try {
          const response = await axios.post(
            `${REST_API}customers/update-location`,
            { latitude, longitude, user_id: user?.id },
            { withCredentials: true }
          );

          if (response?.data?.status === 1) {
            locationName = response.data.data?.current_location_name || null;
          }
        } catch (error) {
          console.error('Error updating customer location:', error);
        }

        try {
          await shareLocation(latitude, longitude, locationName);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLoading(false);
        enqueueSnackbar(
          error?.code === 1
            ? 'Please allow location permission to share'
            : 'Unable to fetch current location',
          { variant: 'error' }
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  return (
    <Tooltip title="Share current location">
      <span>
        <MIconButton
          onClick={handleShareLocation}
          disabled={loading}
          color={loading ? 'primary' : 'default'}
          sx={{
            mr: -0.75,
            ...(loading && {
              bgcolor: (theme) =>
                alpha(
                  theme.palette.primary.main,
                  theme.palette.action.focusOpacity
                ),
            }),
          }}
        >
          {loading ? (
            <CircularProgress size={18} thickness={5} color="primary" />
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '@keyframes shareLocationPulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 0.55,
                  },
                  '70%': {
                    transform: 'scale(1.55)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'scale(1.55)',
                    opacity: 0,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  animation: 'shareLocationPulse 1.6s ease-out infinite',
                },
              }}
            >
              <Icon icon={pinFill} width={18} height={18} />
              <Box
                sx={{
                  position: 'absolute',
                  right: -3,
                  bottom: -2,
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid #fff',
                  boxShadow: (theme) => theme.customShadows?.z1 || '0 1px 4px rgba(0,0,0,0.18)',
                }}
              >
                <Icon icon={shareFill} width={9} height={9} />
              </Box>
            </Box>
          )}
        </MIconButton>
      </span>
    </Tooltip>
  );
}
